import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Mail, Phone, Search, UserPlus, Building2, Calendar, User, ChevronDown, ChevronUp, CheckCircle, Clock, XCircle, Target, TrendingUp } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  new: { bg: "bg-blue-100", text: "text-blue-800", color: "#3b82f6" },
  contacted: { bg: "bg-yellow-100", text: "text-yellow-800", color: "#eab308" },
  qualified: { bg: "bg-purple-100", text: "text-purple-800", color: "#a855f7" },
  converted: { bg: "bg-green-100", text: "text-green-800", color: "#22c55e" },
  closed: { bg: "bg-emerald-100", text: "text-emerald-800", color: "#10b981" },
  lost: { bg: "bg-slate-100", text: "text-slate-800", color: "#64748b" },
};

export default function PropertySeekers() {
  const [seekers, setSeekers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingSeeker, setEditingSeeker] = useState(null);
  const [deletingSeeker, setDeletingSeeker] = useState(null);
  const [expandedSeeker, setExpandedSeeker] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    contact_number: "",
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [usersRes, leadsRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/properties`, { headers }),
      ]);

      // Filter only users with role "user" (property seekers)
      const propertySeekers = usersRes.data.filter(u => u.role === "user");
      setSeekers(propertySeekers);
      setLeads(leadsRes.data);
      setProperties(propertiesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const getSeekerLeads = (seekerEmail) => {
    return leads.filter(lead => 
      lead.email?.toLowerCase() === seekerEmail?.toLowerCase() ||
      lead.contact_email?.toLowerCase() === seekerEmail?.toLowerCase()
    );
  };

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property?.title || "Unknown Property";
  };

  const getAssignedUser = (userId) => {
    const user = users.find(u => u.id === userId);
    return user;
  };

  const formatRoleName = (role) => {
    switch (role) {
      case "team_leader": return "Team Leader";
      case "sourcing_manager": return "Sourcing Manager";
      case "agent": return "Agent";
      case "admin": return "Admin";
      default: return role;
    }
  };

  // Calculate stats for pie chart
  const getLeadStats = () => {
    const seekerEmails = seekers.map(s => s.email.toLowerCase());
    const seekerLeads = leads.filter(lead => 
      seekerEmails.includes(lead.email?.toLowerCase()) ||
      seekerEmails.includes(lead.contact_email?.toLowerCase())
    );

    const stats = {
      new: 0,
      contacted: 0,
      qualified: 0,
      converted: 0,
      closed: 0,
      lost: 0,
    };

    seekerLeads.forEach(lead => {
      const status = lead.status?.toLowerCase() || "new";
      if (stats.hasOwnProperty(status)) {
        stats[status]++;
      }
    });

    return Object.entries(stats)
      .filter(([_, count]) => count > 0)
      .map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value,
        color: STATUS_COLORS[name]?.color || "#64748b",
      }));
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      
      if (editingSeeker) {
        const updateData = { ...formData, role: "user" };
        if (!updateData.password) delete updateData.password;
        
        await axios.put(`${API}/users/${editingSeeker.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Property seeker updated successfully");
        setShowEditDialog(false);
      } else {
        await axios.post(`${API}/auth/register`, {
          ...formData,
          role: "user",
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Property seeker created successfully");
        setShowAddDialog(false);
      }
      
      setFormData({ email: "", password: "", full_name: "", contact_number: "" });
      setEditingSeeker(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save property seeker");
    }
  };

  const handleEdit = (seeker) => {
    setEditingSeeker(seeker);
    setFormData({
      email: seeker.email,
      password: "",
      full_name: seeker.full_name || "",
      contact_number: seeker.contact_number || "",
    });
    setShowEditDialog(true);
  };

  const handleDelete = async () => {
    if (!deletingSeeker) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/users/${deletingSeeker.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Property seeker deleted successfully");
      setShowDeleteDialog(false);
      setDeletingSeeker(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete property seeker");
    }
  };

  const filteredSeekers = seekers.filter(seeker =>
    seeker.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    seeker.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pieData = getLeadStats();
  const totalLeads = pieData.reduce((sum, item) => sum + item.value, 0);
  const closedLeads = pieData.find(p => p.name === "Closed")?.value || 0;
  const convertedLeads = pieData.find(p => p.name === "Converted")?.value || 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0f2744]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0f2744]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Property Seekers
            </h1>
            <p className="text-slate-500 mt-1">Manage customers and track their journey</p>
          </div>
          <Button
            onClick={() => {
              setFormData({ email: "", password: "", full_name: "", contact_number: "" });
              setShowAddDialog(true);
            }}
            className="bg-[#0f2744] hover:bg-[#1e3a5f] text-white"
            data-testid="add-seeker-button"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add Property Seeker
          </Button>
        </div>

        {/* Stats Cards & Pie Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stats Cards */}
          <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0f2744]">{seekers.length}</p>
                  <p className="text-xs text-slate-500">Total Seekers</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Target className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0f2744]">{totalLeads}</p>
                  <p className="text-xs text-slate-500">Total Leads</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0f2744]">{closedLeads + convertedLeads}</p>
                  <p className="text-xs text-slate-500">Successful</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0f2744]">
                    {totalLeads > 0 ? Math.round(((closedLeads + convertedLeads) / totalLeads) * 100) : 0}%
                  </p>
                  <p className="text-xs text-slate-500">Success Rate</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pie Chart */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-[#0f2744] mb-2">Lead Status Distribution</h3>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={50}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend 
                    layout="vertical" 
                    align="right" 
                    verticalAlign="middle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: '10px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[150px] flex items-center justify-center text-slate-400 text-sm">
                No lead data available
              </div>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Seekers List Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Seeker</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Leads</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredSeekers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <UserPlus className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                      <h3 className="text-lg font-semibold text-slate-600">No property seekers found</h3>
                      <p className="text-slate-400 mt-1">
                        {searchTerm ? "Try a different search term" : "Add your first property seeker"}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredSeekers.map((seeker) => {
                    const seekerLeads = getSeekerLeads(seeker.email);
                    const isExpanded = expandedSeeker === seeker.id;
                    const hasClosedLead = seekerLeads.some(l => l.status?.toLowerCase() === "closed" || l.status?.toLowerCase() === "converted");
                    
                    return (
                      <>
                        <tr 
                          key={seeker.id} 
                          className={`hover:bg-slate-50 transition-colors cursor-pointer ${hasClosedLead ? "bg-emerald-50/50" : ""}`}
                          onClick={() => setExpandedSeeker(isExpanded ? null : seeker.id)}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold ${hasClosedLead ? "bg-emerald-500" : "bg-green-500"}`}>
                                {seeker.full_name ? seeker.full_name.charAt(0).toUpperCase() : seeker.email.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-semibold text-[#0f2744]">{seeker.full_name || "No Name"}</p>
                                {hasClosedLead && (
                                  <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                                    <CheckCircle className="h-3 w-3" />
                                    Converted Customer
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Mail className="h-3 w-3" />
                                {seeker.email}
                              </div>
                              {seeker.contact_number && (
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                  <Phone className="h-3 w-3" />
                                  {seeker.contact_number}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-800 font-semibold text-sm">
                              {seekerLeads.length}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            {seekerLeads.length > 0 ? (
                              <div className="flex flex-wrap gap-1 justify-center">
                                {[...new Set(seekerLeads.map(l => l.status?.toLowerCase() || "new"))].map(status => {
                                  const statusStyle = STATUS_COLORS[status] || STATUS_COLORS.new;
                                  return (
                                    <span key={status} className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                                      {status.charAt(0).toUpperCase() + status.slice(1)}
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm">No leads</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(seeker);
                                }}
                                className="text-slate-400 hover:text-[#0f2744]"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingSeeker(seeker);
                                  setShowDeleteDialog(true);
                                }}
                                className="text-slate-400 hover:text-blue-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-slate-400"
                              >
                                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </Button>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Expanded Row - Lead Details */}
                        {isExpanded && (
                          <tr className="bg-slate-50">
                            <td colSpan={5} className="px-6 py-4">
                              {seekerLeads.length === 0 ? (
                                <div className="text-center py-4 text-slate-500">
                                  No leads or inquiries from this property seeker yet.
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  <h4 className="font-semibold text-[#0f2744] text-sm">Interested Properties & Lead Details</h4>
                                  <div className="grid gap-3">
                                    {seekerLeads.map((lead) => {
                                      const assignedUser = getAssignedUser(lead.assigned_to);
                                      const statusStyle = STATUS_COLORS[lead.status?.toLowerCase()] || STATUS_COLORS.new;
                                      const isClosed = lead.status?.toLowerCase() === "closed" || lead.status?.toLowerCase() === "converted";
                                      
                                      return (
                                        <div 
                                          key={lead.id} 
                                          className={`bg-white rounded-lg border p-4 ${isClosed ? "border-emerald-300 bg-emerald-50" : "border-slate-200"}`}
                                        >
                                          <div className="flex flex-wrap items-start justify-between gap-4">
                                            {/* Property Info */}
                                            <div className="flex items-start gap-3">
                                              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${isClosed ? "bg-emerald-100" : "bg-blue-100"}`}>
                                                <Building2 className={`h-5 w-5 ${isClosed ? "text-emerald-600" : "text-blue-600"}`} />
                                              </div>
                                              <div>
                                                <p className="font-semibold text-[#0f2744]">{getPropertyName(lead.property_id)}</p>
                                                <p className="text-sm text-slate-500">{lead.source || "Direct Inquiry"}</p>
                                                {lead.notes && (
                                                  <p className="text-xs text-slate-400 mt-1 max-w-md truncate">Note: {lead.notes}</p>
                                                )}
                                              </div>
                                            </div>

                                            {/* Status */}
                                            <div className="flex flex-col items-center gap-1">
                                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusStyle.bg} ${statusStyle.text}`}>
                                                {isClosed && <CheckCircle className="h-3 w-3 inline mr-1" />}
                                                {lead.status?.toUpperCase() || "NEW"}
                                              </span>
                                              {lead.ai_score && (
                                                <span className="text-xs text-slate-500">Score: {lead.ai_score}</span>
                                              )}
                                            </div>

                                            {/* Follow-up */}
                                            <div className="flex flex-col gap-1">
                                              <div className="flex items-center gap-2 text-sm">
                                                <Calendar className="h-4 w-4 text-slate-400" />
                                                <span className="text-slate-600">
                                                  {lead.follow_up_date 
                                                    ? new Date(lead.follow_up_date).toLocaleDateString()
                                                    : "No follow-up set"
                                                  }
                                                </span>
                                              </div>
                                              <span className="text-xs text-slate-400">Follow-up Date</span>
                                            </div>

                                            {/* Assigned To (Credit) */}
                                            <div className={`flex flex-col gap-1 p-2 rounded-lg ${isClosed ? "bg-emerald-100" : "bg-slate-100"}`}>
                                              <div className="flex items-center gap-2">
                                                <User className={`h-4 w-4 ${isClosed ? "text-emerald-600" : "text-slate-500"}`} />
                                                <span className={`font-medium text-sm ${isClosed ? "text-emerald-700" : "text-slate-700"}`}>
                                                  {assignedUser?.full_name || "Unassigned"}
                                                </span>
                                              </div>
                                              {assignedUser && (
                                                <span className={`text-xs ${isClosed ? "text-emerald-600" : "text-slate-500"}`}>
                                                  {isClosed ? "Closed by: " : ""}{formatRoleName(assignedUser.role)}
                                                </span>
                                              )}
                                            </div>
                                          </div>

                                          {/* Created Date */}
                                          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                                            <span>Lead created: {new Date(lead.created_at).toLocaleDateString()}</span>
                                            {isClosed && (
                                              <span className="text-emerald-600 font-semibold flex items-center gap-1">
                                                <CheckCircle className="h-3 w-3" />
                                                Successfully Converted
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Seeker Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader style={{ background: 'linear-gradient(to right, #0f2744, #1e3a5f)', color: 'white' }}>
              <DialogTitle style={{ color: 'white' }}>Add Property Seeker</DialogTitle>
              <DialogDescription style={{ color: '#cbd5e1' }}>Create a new customer account.</DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 p-6 bg-white">
              <div>
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <Label htmlFor="contact_number">Contact Number</Label>
                <Input
                  id="contact_number"
                  name="contact_number"
                  value={formData.contact_number}
                  onChange={handleChange}
                  placeholder="+91 98765 43210"
                />
              </div>
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="********"
                />
              </div>

              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#0f2744] hover:bg-[#1e3a5f]">
                  Create Seeker
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Seeker Dialog */}
        <Dialog open={showEditDialog} onOpenChange={() => {
          setShowEditDialog(false);
          setEditingSeeker(null);
          setFormData({ email: "", password: "", full_name: "", contact_number: "" });
        }}>
          <DialogContent>
            <DialogHeader style={{ background: 'linear-gradient(to right, #0f2744, #1e3a5f)', color: 'white' }}>
              <DialogTitle style={{ color: 'white' }}>Edit Property Seeker</DialogTitle>
              <DialogDescription style={{ color: '#cbd5e1' }}>Update customer information.</DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 p-6 bg-white">
              <div>
                <Label htmlFor="edit_full_name">Full Name *</Label>
                <Input
                  id="edit_full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label htmlFor="edit_email">Email *</Label>
                <Input
                  id="edit_email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <Label htmlFor="edit_contact_number">Contact Number</Label>
                <Input
                  id="edit_contact_number"
                  name="contact_number"
                  value={formData.contact_number}
                  onChange={handleChange}
                  placeholder="+91 98765 43210"
                />
              </div>
              <div>
                <Label htmlFor="edit_password">New Password (optional)</Label>
                <Input
                  id="edit_password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Leave blank to keep current"
                />
              </div>

              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#0f2744] hover:bg-[#1e3a5f]">
                  Update Seeker
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Property Seeker</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete {deletingSeeker?.full_name || deletingSeeker?.email}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-[#0f2744] hover:bg-[#1e3a5f]">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
